// Import react elements
import { useContext, useEffect, useMemo, useState } from "react";

// Import context
import { AuthContext } from "global/context/AuthContext";
import { UserContext } from "global/context/UserContext";

// Import api calls
import { updateOrder, addNewOrder, getPodProduct, refundOrder, cancelOrder } from "api/APIStrichCalls";

// Import components
import TwoColumnList from "global/components/TwoColumnList";
import { Grid, GridItem } from "global/components/Grid";
import CreateButton from "global/components/CreateButton";
import ImageCarouselPopup from "global/components/ImageCarouselPopup";
import OrderProgressTimeline from "./OrderProgressTimeline";
import LoadingDotsAnimation from "global/components/loading_ui/LoadingDotsAnimation";
import RequestFulfilmentPopUp from "./RequestFulfilmentPopUp";
import FulfilmentDetailsPopUp from "./FulfilmentDetailsPopUp";
import FulfilmentCostBreakdownTable from "./FulfilmentCostBreakdownTable";
import EarningBreakdown from "global/components/EarningBreakdown";
import { InfoButton } from "global/components/InfoButton";
import RefundOrderPopUp from "./RefundOrderPopUp";
import HelpButton from "global/components/HelpButton";

// Import global hooks
import useFormHandler from "global/hooks/useFormHandler";
import { useToast } from "global/hooks/useToast";
import useIsMobile from "global/hooks/useIsMobile";

// Import hooks
//import useAddressCopy from "../hooks/useAddressCopy";
//import useOrderActions from "../hooks/useOrderActions";
import useCustomisableTableActions from "global/hooks/useCustomisableTableActions";
import useOrderDetails from "../hooks/useOrderDetails";

// Import utility functions
import { DisplayConfirmationDialog } from "global/utilities/DialogBox";
// import { GenerateUniqueTimestampID } from "global/utilities/GenerateUniqueTimestampID"; - Not used now we can't create orders
import { CheckNullDate } from "global/utilities/CheckNullDate";
import { FormatCurrency, FormatToTwoDecimalPlacesString } from "global/utilities/ConvertCurrency";
import { formatDisplayDateTime } from "global/utilities/ConvertDate";

// Import css style sheet
import styles from "./OrderDetailsPopUp.module.css";
import { CloseIcon } from "global/strich_icons/StrichIcons";




// Select the product images which match the configuration purchased by the customer
const getOrderItemDisplayImages = (orderItem) => {

	// Get the resolved production print areas and selected colourway
	const printAreas = Array.isArray(orderItem.production_data?.print_areas) ? orderItem.production_data.print_areas : [];
	const selectedColourway = orderItem.order_item_config?.colourway_code || "";
	const images = Array.isArray(orderItem.images) ? orderItem.images : [];

	// Filter the images according to their colourway and physical rotation configuration
	return images.filter((image) => {

		// Colourway-specific images must match the purchased colourway
		if (image.colourway && image.colourway !== selectedColourway) { return false; }

		// Ordinary product and artwork images have no rotation configuration
		if (!Array.isArray(image.rotation_config) || image.rotation_config.length === 0) { return true; }

		// Every physical print area represented in the mockup must match the ordered configuration
		return image.rotation_config.every((location) => {

			// Find the matching production print area and compare its selected rotation
			return printAreas.some((printArea) => {

				// An absent rotation must not be mistaken for rotation zero
				if (printArea.initial_view_rotation === null || printArea.initial_view_rotation === undefined || printArea.initial_view_rotation === "") { return false; }

				return printArea.product_code === location.product_code &&
					printArea.surface_code === location.surface_code &&
					printArea.print_area_code === location.print_area_code &&
					Number(printArea.initial_view_rotation) === Number(location.rotation);

			});

		});

	});

};


const OrderDetailsPopup = ({
    isOpen,
    onClose,
    orderDetails,
    loadingOrderDetails,
    addingNewOrder,
    setAddingNewOrder,
    fetchOrders,
    ordersData,
}) => {

    // Get the authentication token from the AuthContext (determines whether the user is logged in)
    const { token } = useContext(AuthContext);

    // Set isMobile from use hook 
    const isMobile = useIsMobile();

    // Get data from user context
    const { connectionsData, currencies } = useContext(UserContext);

    // Define toast options
    const { toastSuccess, toastError } = useToast();

    // Get the canonical strich products from UserContext
    const { orderStatuses, canonicalProducts } = useContext(UserContext);

    // Store data for the upload print data pop up
    //const [printDataPopUpDetails, setPrintDataPopUpDetails] = useState(null);

    // Set state for image preview pop up
    //const [uploadPrintDataPopupOpen, setUploadPrintDataPopupOpen] = useState(false);

    // State to open / close pop up when choosing canonical products to add as order item 
    //const [canonicalProductsListPopUp, setCanonicalProductsListPopUp] = useState(false);

    // State to open / close pop up when choosing canonical products to add as order item 
    const [showFulfilments, setShowFulfilments] = useState(false);

    // Set empty state for fulfilment details
    const [fulfilmentDetails, setFulfilmentDetails] = useState([]);

    // Set pop up state for the 'Fulfilment Details' pop up
    const [fulfilmentDetailsPopupOpen, setFulfilmentDetailsPopupOpen] = useState(false);

    // Set pop up state for the 'Request Fulfilment' pop up
    const [requestFulfilmentPopupOpen, setRequestFulfilmentPopupOpen] = useState(false);

    // Set state for storing the requested fulfilment costs
    const [fulfilmentRequestCosts, setFulfilmentRequestCosts] = useState(null);

    // Set state for storing the available service codes for this order's delivery country
    const [serviceCodes, setServiceCodes] = useState([]);

    // Set state for whether a refund request is being sent
    const [refundingOrder, setRefundingOrder] = useState(false);

    // Set state for whether a cancel request is being sent
    const [cancellingOrder, setCancellingOrder] = useState(false);

    // Set state for the selected address card tab
    const [selectedAddressType, setSelectedAddressType] = useState("delivery");

    // Set state for the selected transaction details pop up
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    // Set state for showing the earning details pop up
    const [transactionDetailsPopupOpen, setTransactionDetailsPopupOpen] = useState(false);

    // This stores whether the refund popup is open
    const [refundPopupOpen, setRefundPopupOpen] = useState(false);



    



    // This stores whether shipping should be included in the refund
    const [refundShipping, setRefundShipping] = useState(true);
    
    // Get a fallback for the currency from the 1st order item if the order itself doesn't have a currency value
    const orderCurrencyFromFirstOrderItem = orderDetails?.orderitems ? orderDetails?.orderitems[0]?.currency : null;

    // Set state for image preview pop up
    const [selectedCurrency, setSelectedCurrency] = useState(orderDetails?.currency || orderCurrencyFromFirstOrderItem || "GBP");

    // Get the order currency if there is one, else get it from the first order item
    const orderCurrency = orderDetails?.currency ? orderDetails.currency : orderCurrencyFromFirstOrderItem ? orderCurrencyFromFirstOrderItem : addingNewOrder ? selectedCurrency : "";

    // Format the order transaction balance returned by the API
    const netEarnings = orderDetails?.transaction_balance?.currency ? FormatCurrency(orderDetails.transaction_balance.currency, orderDetails?.transaction_balance?.amount ?? 0) : "";



    // Calculate the total refunded values by adding all the Order Refund transaction values
    const totalRefundedBreakdown = (orderDetails?.transactions || []).reduce((total, transaction) => {

        // Stop if this is not a refund transaction
        if (transaction.type !== "Order Refund") {

            // Return the current total unchanged
            return total;

        }

        // Get the refund breakdown safely
        const refundBreakdown = transaction?.transaction_data?.refund_breakdown || {};

        // Add the refunded excluding tax value
        total.exc_tax += Number(refundBreakdown.exc_tax || 0);

        // Add the refunded tax value
        total.tax += Number(refundBreakdown.tax || 0);

        // Add the refunded including tax value
        total.inc_tax += Number(refundBreakdown.inc_tax || 0);

        // Return the updated total
        return total;

    }, {

        // Set the starting excluding tax total
        exc_tax: 0,

        // Set the starting tax total
        tax: 0,

        // Set the starting including tax total
        inc_tax: 0,

    });

    

    const orderHasBeenFullyRefunded = (orderDetails?.order_values?.total_inc_tax - totalRefundedBreakdown.inc_tax) === 0 || false;



    // Get the parsed transaction data from a transaction row
    const getTransactionData = (transaction) => {

        // Get the transaction data
        let transactionData = transaction?.transaction_data;

        // Parse transaction data if it has been returned as a JSON string
        if (typeof transactionData === "string") {

            try {

                // Parse the transaction data string
                transactionData = JSON.parse(transactionData);

            } catch {

                // Return nothing if the transaction data cannot be parsed
                return null;

            }

        }

        // Return the parsed transaction data
        return transactionData || null;

    };



    // Get the earning breakdown from an earning row
    const getEarningBreakdown = (earning) => {

        // Get the parsed transaction data
        const transactionData = getTransactionData(earning);

        // Return the earning breakdown
        return transactionData?.earning_breakdown || null;

    };



    // Get the refund breakdown from a refund row
    const getRefundBreakdown = (refund) => {

        // Get the parsed transaction data
        const transactionData = getTransactionData(refund);

        // Return the refund breakdown
        return transactionData?.refund_breakdown || null;

    };



    // Open the earning details pop up
    const handleSelectTransaction = (transaction) => {

        // Store the selected earning
        setSelectedTransaction(transaction);

        // Open the pop up
        setTransactionDetailsPopupOpen(true);

    };



    // Custom hook for actioning order/s
    const {
        tableActionsConfig,
    } = useCustomisableTableActions({ tableType: "Orders", tableData: ordersData, fetchTableData: fetchOrders, onClose });



    // Custom hook for actioning order details 
    const {
        handleRequestFulfilment,
        handleGetFulfilmentDetails,
    } = useOrderDetails({ token, setServiceCodes, setFulfilmentDetails, setFulfilmentDetailsPopupOpen, setFulfilmentRequestCosts, setRequestFulfilmentPopupOpen});



    //const generateUniqueKey = () => Math.random().toString(36).substr(2, 9);

    /* Set up the initial form values for a blank orderitem - MVP
    const blankOrderItem = () => ({
        id: "blank_" + generateUniqueKey()
    });
    


    // Set up the initial form values for a prefilled orderitem
    const preFilledOrderItem = (orderItemData) => ({
        ...orderItemData,
        id: "pod_" + generateUniqueKey()
    });
    */



    // This runs async work AFTER render when inputs change
    useEffect(() => {

        // This creates a flag to prevent state updates if unmounted / changed mid-flight
        let isCancelled = false;

        // This defines the async function (effects themselves can’t be async)
        const runSkuChecks = async () => {

            // Extract existing order items from orderDetails, or use an empty array if none exist
            const existingItems = orderDetails?.orderitems ?? [];

            // This checks whether ANY order item sku exists in canonical products - whether as a parent, child or variant
            const orderItemsWhichSkuExistsInCanonicalProducts = await Promise.all(

                // This maps each order item to an async existence check
                existingItems.map(async (orderItem) => {

                    // This calls the API for the sku
                    const canonicalProductExistsResponse = await getPodProduct(token, orderItem?.pod_product_sku);

                    // This returns a boolean for this item
                    //return Boolean(canonicalProductExistsResponse?.success);

                    if (canonicalProductExistsResponse.success) {

                        // Return the order item
                        return orderItem;

                    } else {

                        // Return nothing
                        return null;

                    }

                })

            );
            
            // This removes any null (and undefined) items from the array
            const cleanedOrderItemsThatCanBeFulFilled = (orderItemsWhichSkuExistsInCanonicalProducts || []).filter((item) => item != null);

            // This gives a single boolean: true if length on array is greater than 0
            const anOrderItemCanBeFulfiilled = cleanedOrderItemsThatCanBeFulFilled.length > 0;

            // This exits if the effect was cancelled
            if (isCancelled) {

                // This prevents setting state after unmount / change
                return;

            }

            // Set boolean to show/hide fulfilments
            setShowFulfilments(anOrderItemCanBeFulfiilled);

            // Set fulfilment orders
            //setOrderItemsThatCanBeFulFilled(orderItemsWhichSkuExistsInCanonicalProducts);

        };

        // This calls the async runner
        runSkuChecks();

        // This is the cleanup, called when dependencies change or component unmounts
        return () => {

            // This marks the effect as cancelled
            isCancelled = true;

        };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ token, orderDetails?.orderitems ]);




    // Memoize the initial form values to avoid recalculating unless orderDetails change  - AND format values like prices
    const initialFormValues = useMemo(() => {

        // Extract existing order items from orderDetails, or use an empty array if none exist
        const existingItems = orderDetails?.orderitems ?? [];

        // Go through the existingItems and do a check to see if any have a pod_product_sku value that matches the canonical products
        const anOrderItemCanBeFulfiilled = existingItems.some(orderItem =>
            canonicalProducts?.some(canonical => canonical.sku === orderItem.pod_product_sku)
        );

        // Set boolean to show/hide fulfilments
        setShowFulfilments(anOrderItemCanBeFulfiilled);

        // Format each order item’s price/value fields
        const formattedOrderItems = existingItems?.length > 0

            ? existingItems.map(item => ({
                ...item,

                // Format fields like 'itempriceexctax' (to two decial places)
                itempriceexctax: FormatToTwoDecimalPlacesString(item.itempriceexctax),
                itempricetax: FormatToTwoDecimalPlacesString(item.itempricetax),
                itempriceinctax: FormatToTwoDecimalPlacesString(item.itempriceinctax),

            }))

            // There's no existing order items, so insert a new blank order item - Nah, let's not show a blank one if there isn't any
            // : [blankOrderItem()];
            : [];

        // Return the initial form values
        return {

            // Spread all existing order details into the form state
            ...orderDetails,

            // Format the shipping costs (two decial places)
            shippingcostexctax: FormatToTwoDecimalPlacesString(orderDetails?.shippingcostexctax),
            shippingcosttax: FormatToTwoDecimalPlacesString(orderDetails?.shippingcosttax),
            shippingcostinctax: FormatToTwoDecimalPlacesString(orderDetails?.shippingcostinctax),
                        
            // Use the formatted items
            orderitems: formattedOrderItems
        };

        // Dependency array to recompute initialFormValues only when orderDetails change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orderDetails]);



    // Extract form-related state and functions from the `useFormHandler` custom hook
    const {

        formValues,     // Stores the current values of form inputs
        handleChange,   // Function to update form values when inputs change
        handleSubmit,   // Function to handle form submission logic
        resetForm,      // Function to reset data on the form
        isFormPartlyEdited, // Check to see if form has been partially edited
        fieldErrors,    // Object storing validation errors for individual fields
        //formError,      // General error message (e.g. API errors)
        loading,        // Boolean indicating whether the form is submitting
        apiResponse,    // Stores the response from the API after submission

    } = useFormHandler({

        // Default values for the form fields
        initialValues: initialFormValues,

        // Enable syncing - because we're using memo to remeber the initial form values before the user edits them
        syncInitialValues: true,

        // API function to be called on form submission based on adding a new order or updating an existing one
        apiCall: addingNewOrder ? addNewOrder : updateOrder,

        // We want to convert boolean to string
        convertBooleans: true,

        // We want to convert number strings to numbers
        convertNumbers: true,

        // We want to clean up orderitems
        cleanOrderItems: true,

        // Validation function to check the form fields before submission
        validate: (values) => {

            //console.log("error values: ", values);

            // Set errors to empty
            const errors = {};

            // Validation rules for form fields

            // Check if connid is missing OR if it's a string that is empty or just whitespace
            if (
                values.connid === undefined || // connid is missing
                values.connid === null || // connid is explicitly null
                (typeof values.connid === 'string' && !values.connid.trim()) // connid is an empty string or just spaces
            ) {
                // Set the validation error message
                errors.connid = "*Connection is required";
                
            }

            // Require a venue order id
            if (
                values.venueorderid === undefined ||
                values.venueorderid === null ||
                (typeof values.venueorderid === 'string' && !values.venueorderid?.trim())
            ) { errors.venueorderid = "*Venue Order ID is required"; }

            // Don't allow spaces in venueorderid
            if (values.venueorderid?.includes(' ')) {
                errors.venueorderid = '*Spaces are not allowed in this field.';
            }
            // Check for an existing order with matching venue order id and connection id
            const duplicateOrder = ordersData?.find(order =>
                order.venueorderid === values.venueorderid?.trim() && Number(order.connid) === Number(values.connid)
            );
            // I fthis is a new order and a match is found, add a validation error
            if (addingNewOrder && duplicateOrder) {
                errors.venueorderid = '*An order with this Venue Order ID and Connnection already exists.';
            }

            if (!values.status || !values.status?.trim()) errors.status = "*Status is required";

            // Add something for order items
            //if (!values.venueorderitemid || !values.venueorderitemid.trim()) errors.venueorderitemid = "venueorderitemid is required";

            // Return validation errors (if any)
            return errors;

        },

    });

    // useEffect runs whenever `apiResponse` changes (from useFormHandler)
    useEffect(() => {

        // Check if there is a response
        if (apiResponse) {

            // Check if it is successful
            if (apiResponse.success) {

                // Check if we're adding a new order
                if (addingNewOrder) {

                    // We've just added a new order

                    // Close the pop up
                    onClose();

                    // Set to false beacuse we're not adding a new order anymore - if we have a function for it
                    if (setAddingNewOrder) {

                        setAddingNewOrder(false);

                    }

                    // Toast success message
                    toastSuccess({ title: "Success", message: "New order has been added." });

                } else {

                    // We've just updated an existing order

                    // Toast success message
                    toastSuccess({ title: "Success", message: "Order updated." });

                }

                if (fetchOrders) {

                    // Refresh the orders table list because we've added/edited an order - send the current selected order id so it stays in the popup
                    fetchOrders(orderDetails.id);

                }

            } else {

                // Api response success is false

                // Toast error message
                toastError({ title: "Error", message: "Could not save order:" + apiResponse.error_message, autoCloseMs: 6000 });

            }

        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiResponse]);



    // Hook functions to handle copying address values i.e. clone billing to delivery - Taken out MVP 
    //const { areFieldsIdentical, copyAddressFields, copyMessage } = useAddressCopy(formValues, handleChange);



    // Look up connection name based on connid
    const matchedConnection = connectionsData?.find(

        (conn) => String(conn.id) === String(formValues.connid)

    );
    

    // Look up shipping method name based on matched connection shipping methods
    const matchedShippingMethod = matchedConnection?.shipping_methods?.find(

        (method) => String(method.code) === String(formValues.venueshippingmethod)

    );


    // Get the existing delete action from the order actions config
    const deleteOrderAction = tableActionsConfig?.find((action) => action.id === "delete");



    // Add orderitems prices together and format it nicely (£ and 2 decimal places)
    const totalPriceFormatted = (value_type) => {

        // Return a formatted string of a the total, example: £12.00
        return (

            // Use reduce to loop through each order item and accumulate the total
            `${orderDetails?.orderitems?.reduce(

                // Set 'sum' as the accumulator that stores the running total
                (sum, item) =>

                    // Access the property (e.g. item["price"]) and convert to a number (or set as 0)
                    sum + Number(item[value_type] || 0),

                // Initial value to start with
                0

                // Format to 2 decimal places
            ).toFixed(2)}`

        )

    };



    // Handle closing the popup and warn before discarding unsaved changes
    const handleCloseButton = (dialogMessage) => {

        // Check if the form has been partly edited
        if (isFormPartlyEdited()) {

            // It has

            // Display a confrimation dialog
            DisplayConfirmationDialog(

                // Dialog message
                dialogMessage,

                // Confirm button title
                "Yes, discard changes",

                // Cancel button title
                "No, keep editing",

                // Returned response from the dialog box
                function (is_confirmed) {

                    // Check if the response is TRUE
                    if (is_confirmed) {

                        // It is

                        // Reset the form values
                        resetForm(initialFormValues);

                        // Close the pop up
                        onClose();

                    }

                }

            );

        } else {

            // Reset the form values
            resetForm(initialFormValues);

            // Close the pop up
            onClose();

        }

    };


    /* MVP
    // Custom input onChange handler for nested arrays like orderitems - Taken out MVP 
    const handleOrderItemChange = (index, field, value) => {

        // Create a shallow copy of the orderitems array
        const updatedItems = [...formValues.orderitems];

        // Create a shallow copy of the specific item being updated
        const updatedItem = {
            ...updatedItems[index],
            // Apply the new value immutably - otherwise react won't consider the nested order item/s as edited
            [field]: value,
        };

        // Replace the old item in the array
        updatedItems[index] = updatedItem;

        // Trigger state update via existing handleChange - now React detects a change and re-renders
        handleChange({

            target: {
                name: "orderitems",
                value: updatedItems,
            },

        });

    };
    */


    /* MVP
    // Button to add a new row in the order items table  - NOT NEEDED MVP
    const handleAddOrderItemRow = (orderItemData) => {

        handleChange({

            target: {
                name: "orderitems",
                value: [...formValues.orderitems, blankOrderItem()],
            },

        });

    };
   
    // Button to add a new Print on Demand Product row in the order items table - NOT NEEDED MVP
    const handleAddPodItemRow = () => {

        // Set pop up state to true - to show POD products to choose from
        setCanonicalProductsListPopUp(true);

    };
    */


    /* MVP
    // Handle click on pop up - 'selected' POD products to add as new order item/s
    const handleAddPODProductOrderItems = (selectedPodProducts) => {


        // Create new order item rows for all the selected products
        const newPodProductOrderItemRows = selectedPodProducts.map((podProduct) =>

            preFilledOrderItem({
                pod_product_sku: podProduct,
                title: canonicalProducts?.find(product => product.SKU === podProduct)?.Title
            })

        );

        // Extract existing order items
        const existingOrderItems = formValues.orderitems;

        // Check if the only existing item is a blank row
        const shouldRemoveExistingRow = existingOrderItems.length === 1 && (() => {
            const onlyItem = existingOrderItems[0];

            // Check: id starts with 'key'
            const hasKeyPrefix = String(onlyItem.id).startsWith('blank');

            // Check: object only has 'id' as its key
            const hasOnlyIdKey = Object.keys(onlyItem).length === 1 && Object.keys(onlyItem)[0] === 'id';

            return hasKeyPrefix && hasOnlyIdKey;

        })();

        // Build the updated order item array
        const updatedOrderItems = shouldRemoveExistingRow
            ? [...newPodProductOrderItemRows] // If blank, remove it and just use the new rows
            : [...existingOrderItems, ...newPodProductOrderItemRows]; // Otherwise, keep existing

        // Update the form values with the new POD product rows
        handleChange({

            target: {
                name: "orderitems",

                // ... to flatten the array of new rows to add
                value: updatedOrderItems,
            },

        });

        // Close the pop up
        setCanonicalProductsListPopUp(false);

    };
    */


    /* MVP
    // Handle opening the 'upload print data' pop up (per order item)
    const handleOpenUploadPrintDataPopup = (orderItemID, formValuesID, matchedSKU, existingPrintAreasData) => {

        // Set the pop up data
        setPrintDataPopUpDetails({
            orderItemId: orderItemID,
            orderId: formValuesID,
            canonicalProductSku: matchedSKU,
            existingPrintAreasData
        });

        // Open the pop up
        setUploadPrintDataPopupOpen(true);

    };
    */


    /* MVP
    // Component for displayoing image carousel
    const generateImageCarousel = (printAreasData) => {

        //console.log(printAreasData);

        const images = printAreasData.map((printArea, index) => ({ url: printArea.mockup, ordering: index, imageTitle: printArea.print_area_code }))


        return (

            <ImageCarousel images={images} />

        )

    };
    */



    // Set state for image preview pop up
    const [imagePreviewPopupOpen, setImagePreviewPopupOpen] = useState(false);

    // Store order item images
    const [orderItemImages, setOrderItemImages] = useState(null);

    // Set state for the selected order item image
    const [selectedOrderItemImage, setSelectedOrderItemImage] = useState(null);

    // Handle click on order item image
    const openImagePreviewPopup = (mainImageUrl, images) => {

        // Set the order item images to the ones we've just clicked to see
        setOrderItemImages(images);

        // Set the selected image to the one we've passed in (clicked on)
        setSelectedOrderItemImage(mainImageUrl);

        // Open the large image preview pop up
        setImagePreviewPopupOpen(true);

    };



    /* MVP
    // Component for displaying image thumbnail in order items when there is print data
    const displayOrderItemImageThumbnailEditPrintData = (printAreasData, orderItemID, formValuesID, matchedSKU) => {

        //console.log(printAreasData);

        const images = printAreasData?.map((printArea, index) => ({ url: printArea.mockup, ordering: index, imageTitle: printArea.print_area_code }))
        
        const thumbnailImage = printAreasData[0];

        //* Click event to view image/s larger in a pop up carousel 
        return (

            <div className={styles.orderitem_img_container}>

                <div className={styles.orderitem_img_edit_button} title="Edit Print Data" onClick={() => handleOpenUploadPrintDataPopup(orderItemID, formValuesID, matchedSKU, printAreasData)}>🖉</div>

                
                <div style={{ cursor: "pointer" }} title="🔍 Click to Zoom" onClick={() => openImagePreviewPopup(thumbnailImage.mockup, images) }>

                    <img src={thumbnailImage.mockup} alt={thumbnailImage.print_area_code} />

                </div>

            </div>

        )

    };
    */

    /* MVP
    // Component for displaying image thumbnail in order items when there is no print data but it is a saved POD product
    const displayOrderItemImageThumbnailAddPrintData = (orderItemID, formValuesID, matchedSKU) => {

        return (

            <div className={styles.orderitem_img_container}>

                <div
                    className="add-image-placeholder"
                    onClick={() => handleOpenUploadPrintDataPopup(orderItemID, formValuesID, matchedSKU)}
                >
                    +
                    <span>Add Print Data</span>
                </div>
            

            </div>

        )

    };
    */

    /* MVP
    // Component for displaying image thumbnail in order items when there is no print data but it is a POD product, but it hasn't been saved yet
    const displayOrderItemImageThumbnailSaveToAddPrintData = () => {

        // Function to handle click on thumbnail - to save orderitem
        const handleSaveBeforeAddingPrintData = (e) => {

            // Display a confrimation dialog
            DisplayConfirmationDialog(

                // Dialog message
                "This order item must be saved to the order before you can add print data to it.<br/ ><br/ >Would you like to save this order item?",

                // Confirm button title
                "Save",

                // Cancel button title
                "Cancel",

                // Returned response from the dialog box
                function(is_confirmed) {

                // Check if the response is TRUE
                if (is_confirmed) {

                    // It is so save the order
                    handleSubmit(e);

                    }

                }

            );

        };

        return (

            <div className={styles.orderitem_img_container}>

                <div
                    className="add-image-placeholder"
                    onClick={(e) => handleSaveBeforeAddingPrintData(e)}
                >          
                    +
                    <span>Add Print Data <br /> (Save First)</span>

                </div>

            </div>

        )

    };
    */



    // Function to request an order refund
    const handleRefundOrder = async (refundAmount) => {

        // Check the amount is numeric
        if (isNaN(Number(refundAmount))) {

            // Show an error
            toastError({ title: "Error", message: "Refund amount must be numeric.", autoCloseMs: 6000 });

            // Stop processing
            return;

        }

        // Check the amount is greater than zero
        if (Number(refundAmount) <= 0) {

            // Show an error
            toastError({ title: "Error", message: "Refund amount must be greater than zero.", autoCloseMs: 6000 });

            // Stop processing
            return;

        }

        // Build the confirmation amount text
        const refundAmountText = FormatCurrency(orderCurrency, refundAmount);

        // Display the confirmation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "This will request a refund for " + refundAmountText + ".<br/><br/>The order will update when the payment provider confirms the refund.<br/><br/>Are you sure you want to continue?",

            // Confirm button title
            "Yes, request refund",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {

                // Stop if not confirmed
                if (!is_confirmed) return;

                // Set refunding state
                setRefundingOrder(true);

                try {

                    // Build the refund payload
                    const refundPayload = {};

                    // Add the order id
                    refundPayload.order_id = formValues.id;

                    // Add the refund amount
                    refundPayload.amount = Number(refundAmount).toFixed(2);

                    // Send the refund request
                    const response = await refundOrder(token, refundPayload);

                    // Check if the refund request was accepted
                    if (response.success) {

                        // Build the note text
                        const refundNote = `Refunded ${FormatCurrency(orderCurrency, refundPayload.amount)} on ${new Date().toLocaleDateString("en-GB")}`;

                        // Build the updated notes value
                        const updatedOrderNotes = formValues.order_notes ? `${formValues.order_notes}\n${refundNote}` : refundNote;

                        // Update the local notes field
                        handleChange({ target: { name: "order_notes", value: updatedOrderNotes } });

                        // Show success message
                        toastSuccess({ title: "Success", message: "Refund requested." });

                        // Close the refund popup
                        setRefundPopupOpen(false);

                        // Refresh the order details
                        if (fetchOrders) { fetchOrders(orderDetails.id); }

                    } else {

                        // Show API error
                        toastError({ title: "Error", message: "Could not request refund: " + response.error_message, autoCloseMs: 6000 });

                    }

                } catch (error) {

                    // Log the API error
                    console.error("API error:", error);

                    // Show error message
                    toastError({ title: "Error", message: "Could not request refund.", autoCloseMs: 6000 });

                }
            
            // Clear refunding state
            setRefundingOrder(false);

            }

        );
       
    };



    // Function to request an order refund
    const handleCancelOrder = async (orderID) => {

        // Check the id is there
        if (!orderID) { 

            // Show an error
            toastError({ title: "Error", message: "Order ID must be provided.", autoCloseMs: 6000 });

            // Stop processing
            return;

        }

        // Display the confirmation dialog
        DisplayConfirmationDialog(

            // Dialog message
            "<h3>Are you sure you?</h3><br/>This will cancel the order and request a refund.<br/>The order will update when the payment provider confirms the refund.",

            // Confirm button title
            "Yes, cancel order",

            // Cancel button title
            "Cancel",

            // Returned response from the dialog box
            async function (is_confirmed) {

                // Stop if not confirmed
                if (!is_confirmed) return;

                // Set refunding state
                setCancellingOrder(true);

                try {

                    const payload = {
                        "order_id": orderID,
                        "reason": "Customer requested cancellation"
                    }


                    // Send the refund request
                    const response = await cancelOrder(token, payload);

                    // Check if the refund request was accepted
                    if (response.success) {

                        // Show success message
                        toastSuccess({ title: "Success", message: "Ordered " + orderID + " cancelled." });

                        // Refresh the order details
                        if (fetchOrders) { fetchOrders(orderID); }

                    } else {

                        // Show API error
                        toastError({ title: "Error", message: "Could not cancel order.", apiErrorMessage: response.error_message, autoCloseMs: 6000 });

                    }

                } catch (error) {

                    // Log the API error
                    console.error("API error:", error);

                    // Show error message
                    toastError({ title: "Error", message: "Could not cancel order.", autoCloseMs: 6000 });

                }
            
            // Clear refunding state
            setCancellingOrder(false);

            }

        );
       
    };



    // If isOpen is false return early (don't show the pop up)
    if (!isOpen) return null;



    // Otherwise show the pop up
    return (

        <div className="popup-overlay">

            <div className="popup-container popup-container--large">

                {/* This is the modal header */}
                <div className="popup__header">

                    {/* This is the title area */}
                    <div className="popup__headerLeft horizontal-align-content-center">

                        <div>

                            {/* This is the parent title */}
                            <h2 className="popup__title">

                                Order {!addingNewOrder && `- ${orderDetails?.id}`}

                            </h2>

                            {/* This is the parent subtitle */}
                            <p className="popup__subtitle">
                               

                            </p>

                        </div>

                        {!addingNewOrder &&

                            <div className={styles.order_header_summary}>

                                {/* Show the current order status */}
                                <span className={styles.order_status_badge}>{formValues.status || "No status"}</span>

                                {/* Show the connection name */}
                                {matchedConnection?.connectionname && <span>{matchedConnection.connectionname}</span>}

                            </div>

                        }
                            
                    </div>

                    

                    {/* This is the header actions */}
                    <div className="popup__headerRight">

                        {/* This is the close button */}
                        <button type="button" className="popup__close" onClick={() => handleCloseButton("You have unsaved changes. Are you sure you want to close?")} title="Close">
                            <CloseIcon />    
                        </button>

                    </div>

                </div>

                <br />

                <div className="tabs-wrapper tabs-wrapper--no-background">

                    <div className="popup-content">

                        {(loadingOrderDetails || !orderDetails) ? 
                        
                            <p>Loading<LoadingDotsAnimation /></p>

                            :

                            <>
                                <form onSubmit={handleSubmit}>

                                    <Grid className={"popup_grid-container"}>

                                        <OrderProgressTimeline
                                            formValues={formValues}
                                            handleChange={handleChange}
                                            CheckNullDate={CheckNullDate}
                                        />

                                    </Grid>

                                    

                                    <div className={styles.order_details_two_column_layout}>

                                        <div className={styles.order_details_left_column}>

                                            <Grid className={"popup_grid-container"} >

                                                <GridItem>

                                            <div>

                                                {addingNewOrder ?

                                                    <>

                                                        <h3>New Order Details</h3>

                                                        <TwoColumnList property={<label>Connection</label>} value={
                                                            <select
                                                                name="connid"
                                                                value={formValues.connid || ""}
                                                                onChange={handleChange}    
                                                            >
                                                                <option value="">Select a connection</option>
                                                                {connectionsData.map((conn) => (
                                                                    <option key={conn.id} value={conn.id}>
                                                                        {conn.connectionname}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        } />

                                                        {fieldErrors.connid && <p style={{ color: "red" }}>{fieldErrors.connid}</p>}

                                                        <TwoColumnList property={<label>Venue Order ID</label>} value={<input name="venueorderid" type="text" value={formValues.venueorderid || ""} onChange={handleChange} placeholder="Venue Order ID" />} />

                                                        {fieldErrors.venueorderid && <p style={{ color: "red" }}>{fieldErrors.venueorderid}</p>}

                                                        <TwoColumnList property={<label>Status</label>} value={
                                                            <select
                                                                name="status"
                                                                value={formValues.status || ""}
                                                                onChange={handleChange}
                                                            >
                                                                <option value="">Select...</option>
                                                                
                                                                {(orderStatuses || []).map((status) =>

                                                                    // Map through orderStatuses to create the dropdown
                                                                    <option key={status} value={status}>{status}</option>

                                                                )}

                                                            </select>
                                                        } />

                                                        {fieldErrors.status && <p style={{ color: "red" }}>{fieldErrors.status}</p>}

                                                        <TwoColumnList property={<label>Currency</label>} value={
                                                            <select
                                                                name="visualType" 
                                                                value={selectedCurrency}
                                                                onChange={(e) => {
                                                                    
                                                                    setSelectedCurrency(e.target.value);

                                                                    handleChange(e);

                                                                }}
                                                                style={{width: "auto", padding: "2px 8px"}}
                                                            >

                                                                <option value="GBP">GBP</option>
                                                                <option value="USD">USD</option>
                                                                <option value="EUR">EUR</option>

                                                            </select>
                                                        } />

                                                    </>

                                                    :

                                                    <>

                                                        <h3>Summary <HelpButton section="orders" code="order-details" label="Order Details" /></h3>

                                                        <div className={styles.order_summary}>

                                                            {/* Show the summary column headings */}
                                                            <div className={styles.order_summary_header}>
                                                                <span></span>
                                                                <span>Exc.</span>
                                                                <span>Tax</span>
                                                                <span>Inc.</span>
                                                            </div>

                                                            {/* Show the item totals */}
                                                            <div className={styles.order_summary_row}>
                                                                <span>Items</span>
                                                                <strong>{FormatCurrency(orderCurrency, totalPriceFormatted("itempriceexctax"))}</strong>
                                                                <strong>{FormatCurrency(orderCurrency, totalPriceFormatted("itempricetax"))}</strong>
                                                                <strong>{FormatCurrency(orderCurrency, totalPriceFormatted("itempriceinctax"))}</strong>
                                                            </div>

                                                            {/* Show the shipping totals */}
                                                            <div className={styles.order_summary_row}>
                                                                <span>Shipping</span>
                                                                <strong>{FormatCurrency(orderCurrency, formValues?.shippingcostexctax || 0)}</strong>
                                                                <strong>{FormatCurrency(orderCurrency, formValues?.shippingcosttax || 0)}</strong>
                                                                <strong>{FormatCurrency(orderCurrency, formValues?.shippingcostinctax || 0)}</strong>
                                                            </div>

                                                            {/* Show duties if there are any */}
                                                            {(formValues?.order_values?.import_duty !== 0 ) && (formValues?.order_values?.import_duty !== "0" ) &&

                                                                <div className={styles.order_summary_row}>
                                                                    <span>Import Duty</span>
                                                                    <strong></strong>
                                                                    <strong></strong>
                                                                    <strong>{FormatCurrency(orderCurrency, formValues?.order_values?.import_duty || 0)}</strong>
                                                                </div>

                                                            }

                                                            {/* Show any refund totals */}
                                                            {orderDetails?.transactions?.find((transaction) => transaction.type === "Order Refund") &&
                                                            
                                                                <div className={styles.order_summary_row}>
                                                                    <span>Refund</span>
                                                                    <strong className={styles.order_summary_total_refund}>- {FormatCurrency(orderCurrency, totalRefundedBreakdown.exc_tax)}</strong>
                                                                    <strong className={styles.order_summary_total_refund}>- {FormatCurrency(orderCurrency, totalRefundedBreakdown.tax)}</strong>
                                                                    <strong className={styles.order_summary_total_refund}>- {FormatCurrency(orderCurrency, totalRefundedBreakdown.inc_tax)}</strong>
                                                                </div>

                                                            }

                                                            {/* Show the full order total */}
                                                            <div className={styles.order_summary_total}>
                                                                <span>Total</span>
                                                                <strong>{FormatCurrency(orderCurrency, ((formValues?.order_values?.total_inc_tax || 0) - totalRefundedBreakdown.inc_tax ) )}</strong>
                                                            </div>

                                                            {/* Show the net earnings */}
                                                            {netEarnings !== "" && (

                                                                <div className={styles.order_summary_total}>
                                                                    <span>Net Earnings</span>
                                                                    <strong>{netEarnings}</strong>
                                                                </div>

                                                            )}

                                                        </div>

                                                    </>

                                                }

                                            </div>
                                        

                                        </GridItem>

                                         <GridItem>

                                            <div>

                                                <div className="horizontal-align-content-center flex-justify-left-right order-details__address-header">

                                                    <h3>Address</h3>

                                                    <div className={styles.address_tab_buttons}>

                                                        {/* Show the shipping address tab */}
                                                        <CreateButton
                                                            title="Shipping"
                                                            size="xs"
                                                            type="button"
                                                            plain={selectedAddressType !== "delivery"}
                                                            onClick={() => setSelectedAddressType("delivery")}
                                                        />

                                                        {/* Show the billing address tab */}
                                                        <CreateButton
                                                            title="Billing"
                                                            size="xs"
                                                            type="button"
                                                            plain={selectedAddressType !== "billing"}
                                                            onClick={() => setSelectedAddressType("billing")}
                                                        />

                                                    </div>

                                                </div>

                                                <br />
                                                
                                                <div>

                                                    <div className="horizontal-align-content-center flex-justify-left-right">

                                                        <h4>{selectedAddressType === "billing" ? "Billing" : "Shipping"} Address</h4>


                                                        {/* Taken out - MPV - can't make own orders yet or edit strich orders
                                                        {copyMessage.delivery ?

                                                            <CreateButton
                                                                title={copyMessage.delivery}
                                                                size="xs"
                                                                type="button"
                                                                plain={true}
                                                            />

                                                            :

                                                            <CreateButton
                                                                title="⇄ Use for Billing"
                                                                size="xs"
                                                                type="button"
                                                                plain={true}
                                                                onClick={() => copyAddressFields('delivery', 'billing')}
                                                                disabled={areFieldsIdentical('delivery', 'billing')}
                                                            />

                                                        }
                                                        */}
                                                                                                            
                                                    </div>

                                                    <br />

                                                    <TwoColumnList property={<label>Company Name</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingcompanyname" type="text" value={formValues.billingcompanyname || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliverycompanyname" type="text" value={formValues.deliverycompanyname || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label>Customer Name</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingcustname" type="text" value={formValues.billingcustname || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliverycustname" type="text" value={formValues.deliverycustname || ""} onChange={handleChange} placeholder="" disabled />   
                                                    } />
                                                    <TwoColumnList property={<label>Street Address</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingaddr1" type="text" value={formValues.billingaddr1 || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliveryaddr1" type="text" value={formValues.deliveryaddr1 || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label> </label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingaddr2" type="text" value={formValues.billingaddr2 || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliveryaddr2" type="text" value={formValues.deliveryaddr2 || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label> </label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingaddr3" type="text" value={formValues.billingaddr3 || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliveryaddr3" type="text" value={formValues.deliveryaddr3 || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label>Town / City</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingaddr4" type="text" value={formValues.billingaddr4 || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliveryaddr4" type="text" value={formValues.deliveryaddr4 || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label>Postcode</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingpostcode" type="text" value={formValues.billingpostcode || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliverypostcode" type="text" value={formValues.deliverypostcode || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label>Country</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingcountry" type="text" value={formValues.billingcountry || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliverycountry" type="text" value={formValues.deliverycountry || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />
                                                    <TwoColumnList property={<label>Phone</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingcontacttel" type="text" value={formValues.billingcontacttel || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliverycontacttel" type="text" value={formValues.deliverycontacttel || ""} onChange={handleChange} placeholder="" disabled />   
                                                    } /> 
                                                    <TwoColumnList property={<label>Email</label>} value={
                                                        selectedAddressType === "billing" ?
                                                        <input name="billingemail" type="text" value={formValues.billingemail || ""} onChange={handleChange} placeholder="" disabled />
                                                        :
                                                        <input name="deliveryemail" type="text" value={formValues.deliveryemail || ""} onChange={handleChange} placeholder="" disabled />
                                                    } />

                                                        {/* Delivery Country select taken out - MVP
                                                        <select
                                                            name="deliverycountry"
                                                            value={formValues.deliverycountry || ""}
                                                            onChange={handleChange}
                                                            disabled
                                                        >
                                                            <option value="">Select...</option>

                                                            {(fulfilmentCountries || []).map((country) =>

                                                                // Map through fulfilmentCountries to create the dropdown
                                                                <option key={country.name} value={country.name}>{country.name}</option>

                                                            )}
                                                            
                                                        </select>
                                                        */}

                                                </div>


                                            </div>

                                        </GridItem>


                                        {/* 
                                        <GridItem>

                                            <h3>Order Notes</h3>
                                            <br /> 
                                            <textarea name="order_notes" rows="2" cols="30" style={{maxWidth: "100%"}} value={formValues.order_notes || ""} onChange={handleChange} ></textarea>
                                        
                                        </GridItem>
                                        */} 


                                            </Grid>

                                            {/* If we're adding a new order don't show fulfilments */}
                                            {!addingNewOrder && showFulfilments &&

                                                <Grid className={"popup_grid-container"}>

                                                    <GridItem className={"scroll-x"}>

                                                        <div className="horizontal-align-content-center flex-justify-left-right">

                                                            <h3>Fulfilments <HelpButton section="orders" code="fulfilment" label="Requesting Fulfilment" /></h3>

                                                            {orderDetails ? (

                                                                <CreateButton title="Request Fulfilment" size="m" onClick={() => handleRequestFulfilment(orderDetails.id, orderDetails.deliverycountry)} />

                                                            ) : ""}

                                                        </div>

                                                        {/* Check if there are any fulfilments for this order  - if so display them in a table*/}
                                                        {Array.isArray(orderDetails?.fulfilments) && orderDetails?.fulfilments?.length !== 0 ? (

                                                            <div className={styles.table_container}>

                                                                <table className={styles.fulfilments_table}>

                                                                    <thead>

                                                                        <tr>
                                                                            <th></th>
                                                                            <th>Requested Date</th>
                                                                            <th>Status</th>
                                                                        </tr>

                                                                    </thead>

                                                                    <tbody>

                                                                        {/* Map orderDetails.fulfilments[*].order.orderitems if available */}
                                                                        {orderDetails.fulfilments.sort((a, b) => new Date(b.requested_date) - new Date(a.requested_date)).map((fulfilmentItem) => (

                                                                            <tr key={fulfilmentItem.id}
                                                                                title={fulfilmentItem.order ? "View fulfilment details" : ""}
                                                                                style={{cursor: fulfilmentItem.order ? "pointer" : "auto" }}
                                                                                onClick={() => fulfilmentItem.order ? handleGetFulfilmentDetails(fulfilmentItem) : ""}
                                                                            >
                                                                                <td>
                                                                                  
                                                                                </td>
                                                                                <td>{formatDisplayDateTime(fulfilmentItem.requested_date) || ""}</td>
                                                                                <td>{fulfilmentItem.status || ""}</td>
                                                                            </tr>

                                                                        ))}

                                                                    </tbody>

                                                                </table>

                                                            </div>

                                                        ) : ""}

                                                    </GridItem>

                                                </Grid>

                                            }

                                        </div>

                                        <div className={styles.order_details_right_column}>


                                            <Grid className={"popup_grid-container"}>

                                                <GridItem>

                                                    <div>

                                                        <div className={styles.shipping_title_row}>

                                                            {/* Show the shipping card title */}
                                                            <h3>Shipping</h3>

                                                            <div className="horizontal-align-content-center">

                                                                {/* Show the last updated tracking timestamp if it's not null */}
                                                                {CheckNullDate(formValues.tracking_updated) && (

                                                                    <span>Last updated: {formValues.tracking_updated}</span>

                                                                )}

                                                            </div>

                                                        </div>
                                                            
                                                        <div className={styles.shipping_compact_row}>

                                                            {/* Show the chosen shipping method */}
                                                            <span className={styles.shipping_compact_label}>Method</span>
                                                            <span className={styles.shipping_compact_value} title={formValues.venueshippingmethod || ""}>{matchedShippingMethod ? matchedShippingMethod.name : "-"}</span>

                                                            {/* Show the tracking service */}
                                                            <span className={styles.shipping_compact_label}>Service</span>
                                                            <span className={styles.shipping_compact_value} title={formValues.tracking?.service || ""}>{formValues.tracking?.service || "-"}</span>
                                               
                                                            {/* Show the tracking number and link to tracking when a URL is available */}
                                                            <span className={styles.shipping_compact_label}>Tracking No.</span>

                                                            {formValues.tracking?.url ?

                                                                <a
                                                                    className={styles.shipping_compact_value}
                                                                    href={formValues.tracking.url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    title={formValues.trackingnumber || ""}
                                                                >
                                                                    {formValues.trackingnumber || "View Tracking"}
                                                                </a>

                                                                :

                                                                <span className={styles.shipping_compact_value} title={formValues.trackingnumber || ""}>{formValues.trackingnumber || "-"}</span>

                                                            }

                                                            {/* Show the tracking status */}
                                                            <span className={styles.shipping_compact_label}>Status</span>
                                                            <span className={styles.shipping_compact_value} title={formValues.tracking_status || ""}>{formValues.tracking_status || "-"}</span>

                                                        </div>

                                                        <div className={styles.shipping_notes_row}>

                                                            {/* Show the tracking notes label */}
                                                            <span className={styles.shipping_compact_label}>Notes</span>

                                                            {/* Show the tracking notes */}
                                                            <span className={styles.shipping_compact_value} title={formValues.tracking_summary || ""}>{formValues.tracking_summary || "-"}</span>

                                                        </div>

                                                    </div>

                                                </GridItem>

                                                <GridItem className={"scroll-x"}>

                                            <div className="horizontal-align-content-center flex-justify-left-right">

                                                <h3>Order Items <HelpButton section="orders" code="order-details" label="Order Details" /></h3>

                                                {/* Taken out ability for users to create / amend order items - MVP */}

                                                {/* <div className={`horizontal-align-content-center ${styles.add_new_order_item_btn}`}> */}

                                                    {/* Add new blank order item */}
                                                    {/* <CreateButton title="+ Add Item" size="s" type="button" onClick={() => handleAddOrderItemRow()} /> */}

                                                    {/* Add new order item but choose from list of canonical POD products */}
                                                    {/* <CreateButton title="+ Add POD Item" size="s" type="button" onClick={() => handleAddPodItemRow()} /> */}

                                                    {/* Add another option for looking up a specific POD (or product?) SKU and auto populating with the details */}

                                                {/* </div> */}

                                                
                                                
                                            </div>

                                                <div className={styles.table_container}>

                                                <table className={`${styles.order_items_table} ${styles.order_items_table_main}`}>

                                                    <thead>

                                                        <tr>

                                                            <th style={{ minWidth: isMobile ? "70px" : "95px" }}></th>
                                                            <th style={{ minWidth: isMobile ? "160px" : "180px" }} >Title</th>
                                                            <th style={{ minWidth: isMobile ? "45px" :  "70px" }} >Qty</th>
                                                            <th style={{ minWidth: isMobile ? "60px" :  "80px" }} >Excl. Tax</th>
                                                            <th style={{ minWidth: isMobile ? "60px" :  "80px" }} >Tax</th>
                                                            <th style={{ minWidth: isMobile ? "60px" :  "80px" }} >Incl. Tax</th>
                                                          
                                                        </tr>

                                                    </thead>

                                                    <tbody>

                                                        {/* Map orderDetails.orderitems if available and create a table row for each */}
                                                        {formValues.orderitems?.map((orderItem, index) => {

                                                            // Get the readable selected colourway name
                                                            let colourwayName = orderItem.production_data?.colourway?.name || "";

                                                            // Fall back to the saved colourway code if no name is available
                                                            if (!colourwayName) {

                                                                colourwayName = orderItem.order_item_config?.colourway_code || "";

                                                            }

                                                            // Select the images matching the purchased colourway and print-area rotations
                                                            const displayImages = getOrderItemDisplayImages(orderItem);

                                                            return (

                                                                <tr key={orderItem.id || index}>

                                                                    <td>

                                                                        {/* Display the images matching the purchased product configuration */}
                                                                        {displayImages.length > 0 &&

                                                                            <div className={styles.orderitem_img_container}>

                                                                                {/* Open the matching images in the full-size carousel */}
                                                                                <div style={{ cursor: "pointer" }} title="🔍 Click to Zoom" onClick={() => openImagePreviewPopup(displayImages[0].image_url, displayImages) }>

                                                                                    <img src={displayImages[0].image_url} alt={displayImages[0].image_id} />

                                                                                </div>

                                                                            </div>
                                                                            
                                                                        
                                                                        }
                                                                        

                                                                        {/* Check if there's print data and that the item matches a canonical product - MVP
                                                                        {(orderItem.print_data?.print_areas?.length > 0 && matched) ?

                                                                            // Display one image, with click to view more and edit print data option
                                                                            displayOrderItemImageThumbnailEditPrintData(orderItem.print_data.print_areas, orderItem.id, formValues.id, matched.SKU)

                                                                            : ""

                                                                        }
                                                                        */}

                                                                        {/* Check if there's no print data, it matches a canonical product and it's not a new order item row, if so display 'add print data' - MVP
                                                                        {!orderItem.print_data && matched && !isNewRow ?

                                                                            displayOrderItemImageThumbnailAddPrintData(orderItem.id, formValues.id, matched.SKU)

                                                                            :

                                                                            // There's no print data, it matches a canonical product and it's a new order item row, if so display 'save before adding print data'
                                                                            !orderItem.print_data && matched && isNewRow ?

                                                                                displayOrderItemImageThumbnailSaveToAddPrintData()

                                                                                    : ""

                                                                        }
                                                                        */}  

                                                                    </td>

                                                                    <td>

                                                                        {/* Show the order item title */}
                                                                        <span className={styles.order_item_value}>
                                                                            {orderItem.title || ""}
                                                                            <br/>
                                                                            <br/>
                                                                            SKU: {orderItem.sku}
                                                                        </span>

                                                                        {/* Show the selected colourway */}
                                                                        {colourwayName && (

                                                                            <span className={styles.order_item_value}>
                                                                                Colourway: {colourwayName}
                                                                            </span>

                                                                        )}

                                                                    </td>

                                                                    <td><span className={styles.order_item_value}>{orderItem.orderquantity || ""}</span></td>
                                                                    <td><span className={styles.order_item_value}>{FormatCurrency(orderCurrency, (orderItem.itempriceexctax * (orderItem.orderquantity || 1 )) || 0)}</span></td>
                                                                    <td><span className={styles.order_item_value}>{FormatCurrency(orderCurrency, (orderItem.itempricetax * (orderItem.orderquantity || 1 )) || 0)}</span></td>
                                                                    <td><span className={styles.order_item_value}>{FormatCurrency(orderCurrency, (orderItem.itempriceinctax * (orderItem.orderquantity || 1 )) || 0)}</span></td>
                              
                                                                </tr>

                                                            )

                                                        })}

                                                            { /* If we're adding a new order then don't show price totals, otherwise show order item totals */}
                                                            { addingNewOrder ? "" :

                                                                <tr className={styles.order_items_total_row}>
                                                                    
                                                                    <td></td>
                                                                    <td></td>
                                                                    <td></td>
                                                                    <td>{orderDetails ? FormatCurrency(orderCurrency, totalPriceFormatted("itempriceexctax")) : ""}</td>
                                                                    <td>{orderDetails ? FormatCurrency(orderCurrency, totalPriceFormatted("itempricetax")) : ""}</td>
                                                                    <td>{orderDetails ? FormatCurrency(orderCurrency, totalPriceFormatted("itempriceinctax")) : ""}</td>
                                                    
                                                                    
                                                                </tr>

                                                            }

                                                        </tbody>

                                                </table>

                                            </div>

                                        </GridItem>

                                        {/* Show the order earnings if available */}
                                        {Array.isArray(orderDetails?.transactions) && orderDetails.transactions.length > 0 && (

                                            <GridItem className={"scroll-x"}>

                                                <h3>Earnings</h3>

                                                <div className={styles.table_container}>

                                                    <table className={`${styles.order_items_table} ${styles.transactions_table}`}>

                                                        <thead>

                                                            <tr>
                                                                <th style={{ minWidth: "170px" }}>Date</th>
                                                                <th style={{ minWidth: "80px" }}>Type</th>
                                                                <th style={{ minWidth: "110px" }}>Order Value</th>
                                                                <th style={{ minWidth: "130px" }}>Taxes & Tariffs</th>
                                                                <th style={{ minWidth: "90px" }}>Fees</th>
                                                                <th style={{ minWidth: "130px" }}>Fulfilment</th>
                                                                <th style={{ minWidth: "110px" }}>Amount</th>
                                                            </tr>

                                                        </thead>

                                                        <tbody>

                                                            {[...orderDetails.transactions].sort((a, b) => String(a.transaction_date || "").localeCompare(String(b.transaction_date || ""))).map((transaction, index) => {

                                                                // This gets the same earning breakdown used by the earning details popup
                                                                const transaction_breakdown = getEarningBreakdown(transaction);

                                                                // This gets the refund breakdown for refund rows
                                                                const refund_breakdown = getRefundBreakdown(transaction);

                                                                // This stores the order breakdown
                                                                const order = transaction_breakdown?.order || {};

                                                                // This stores the original currency
                                                                const original_currency = refund_breakdown?.refund?.currency || order?.original_currency || transaction.currency;

                                                                // This stores the account currency
                                                                const converted_currency = transaction.currency;

                                                                // This stores the original order values
                                                                const original = order?.totals?.original || {};

                                                                // This stores the fulfilment deduction
                                                                const fulfilment = order?.totals?.deductions?.fulfilment || {};

                                                                // This stores the displayed order value
                                                                const displayOrderValue = refund_breakdown ? -Number(refund_breakdown.inc_tax || 0) : Number(original?.gross || 0);

                                                                // This stores the displayed tax and tariff value
                                                                const displayTaxes = refund_breakdown ? Number(refund_breakdown.tax || 0) : -Number(original?.taxes?.total || 0);

                                                                // This stores the displayed fee value
                                                                const displayFees = transaction_breakdown ? -Number(original?.fees?.total || 0) : 0;

                                                                // This stores the displayed fulfilment charge or refund credit
                                                                let displayFulfilment = transaction_breakdown ? -Number(fulfilment?.total || 0) : 0;

                                                                // Use the fulfilment credit explicitly recorded against the refund
                                                                if (refund_breakdown?.fulfilment_credit?.amount != null) {
                                                                    displayFulfilment = Number(refund_breakdown.fulfilment_credit.amount) / 100;
                                                                }

                                                                return (

                                                                    <tr key={transaction.id || index} className={styles.clickable_table_row} onClick={() => handleSelectTransaction(transaction)}>
                                                                        <td><span className={`${styles.order_item_value} ${styles.earning_date_value}`}>{formatDisplayDateTime(transaction.transaction_date) || ""}</span></td>
                                                                        <td>
                                                                            <span className={`${styles.order_item_value} ${styles.earning_type_cell}`}>
                                                                                <span>{transaction.type ? transaction.type.replace(/_/g, " ") : ""}</span>
                                                                                {refund_breakdown && (
                                                                                    <span
                                                                                        className={styles.earning_info_button}
                                                                                        onClick={(event) => event.stopPropagation()}
                                                                                        onMouseDown={(event) => event.stopPropagation()}
                                                                                    >
                                                                                        <InfoButton
                                                                                            shortLabel="Order refund transaction values"
                                                                                            description="When issuing a refund, some charges and taxes (like VAT) can be reclaimed, so the balance deduction for the refund may be less than the refund issued."
                                                                                        />
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </td>
                                                                        <td><span className={styles.order_item_value}>{original_currency ? FormatCurrency(original_currency, displayOrderValue, false) : ""}</span></td>
                                                                        <td><span className={styles.order_item_value}>{original_currency ? FormatCurrency(original_currency, displayTaxes, false) : ""}</span></td>
                                                                        <td><span className={styles.order_item_value}>{original_currency ? FormatCurrency(original_currency, displayFees, false) : ""}</span></td>
                                                                        <td><span className={styles.order_item_value}>{converted_currency ? FormatCurrency(converted_currency, displayFulfilment, false) : ""}</span></td>
                                                                        <td>
                                                                            <span className={`${styles.order_item_value} ${styles.earning_amount_cell}`}>
                                                                                <span>{transaction.currency ? FormatCurrency(transaction.currency, transaction.amount || 0, true) : ""}</span>
                                                                                {transaction.notes && <span className={styles.earning_notes_icon} title={transaction.notes}>i</span>}
                                                                            </span>
                                                                        </td>
                                                                    </tr>

                                                                );

                                                            })}

                                                            <tr className={styles.transactions_total_row}>
                                                                <td></td>
                                                                <td></td>
                                                                <td></td>
                                                                <td></td>
                                                                <td></td>
                                                                <td></td>
                                                                <td>{FormatCurrency(orderDetails.transactions[0]?.currency || orderCurrency, orderDetails.transactions.reduce((total, transaction) => total + (Number(transaction.amount) || 0), 0), true)}</td>
                                                            </tr>

                                                        </tbody>

                                                    </table>

                                                </div>

                                            </GridItem>

                                        )}

                                                <GridItem>

                                                    <h3>Order Notes</h3>
                                                    <br/>
                                                    <textarea name="order_notes" rows="5" cols="30" className={styles.order_notes_textarea} value={formValues.order_notes || ""} onChange={handleChange} placeholder="Order Notes"></textarea>

                                                </GridItem>

                                            </Grid>

                                        </div>

                                    </div>

                                    {/* Display transaction details pop up */}
                                    {transactionDetailsPopupOpen && selectedTransaction && (

                                        <div className="popup-overlay">

                                            <div className="popup-container popup-container--medium">

                                                {/* This is the modal header */}
                                                <div className="popup__header">

                                                    {/* This is the title area */}
                                                    <div className="popup__headerLeft">

                                                        {/* This is the parent title */}
                                                        <h2 className="popup__title">{selectedTransaction.type} Details</h2>

                                                        {/* This is the parent subtitle */}
                                                        <p className="popup__subtitle">{selectedTransaction.reference || ""}</p>

                                                    </div>

                                                    {/* This is the header actions */}
                                                    <div className="popup__headerRight">

                                                        {/* This is the close button */}
                                                        <button type="button" className="popup__close" onClick={() => { setTransactionDetailsPopupOpen(false); setSelectedTransaction(null); }} title="Close">
                                                            <CloseIcon />
                                                        </button>

                                                    </div>

                                                </div>

                                                <div className="popup-content popup-content__body">

                                                    <div className={styles.transaction_details}>

                                                        <div className={styles.transaction_details_grid}>

                                                            <div className={styles.transaction_details_row}>
                                                                <span className={styles.transaction_details_label}>Type</span>
                                                                <span className={styles.transaction_details_value}>{selectedTransaction.type || ""}</span>
                                                            </div>

                                                            <div className={styles.transaction_details_row}>
                                                                <span className={styles.transaction_details_label}>Amount</span>
                                                                <span className={styles.transaction_details_value}>{selectedTransaction.currency ? FormatCurrency(selectedTransaction.currency, selectedTransaction.amount || 0, true) : ""}</span>
                                                            </div>

                                                            <div className={styles.transaction_details_row}>
                                                                <span className={styles.transaction_details_label}>Date</span>
                                                                <span className={styles.transaction_details_value}>{formatDisplayDateTime(selectedTransaction.transaction_date) || ""}</span>
                                                            </div>

                                                            <div className={styles.transaction_details_row}>
                                                                <span className={styles.transaction_details_label}>Available Date</span>
                                                                <span className={styles.transaction_details_value}>{formatDisplayDateTime(selectedTransaction.available_date) || ""}</span>
                                                            </div>

                                                            <div className={styles.transaction_details_row}>
                                                                <span className={styles.transaction_details_label}>Reference</span>
                                                                <span className={styles.transaction_details_value}>{selectedTransaction.id || ""}</span>
                                                            </div>

                                                            <div className={`${styles.transaction_details_row} ${styles.transaction_details_row_full}`}>
                                                                <span className={styles.transaction_details_label}>Notes</span>
                                                                <span className={styles.transaction_details_value}>{selectedTransaction.notes || "-"}</span>
                                                            </div>

                                                        </div>

                                                        {getEarningBreakdown(selectedTransaction) && (

                                                            <div className={styles.transaction_details_breakdown}>

                                                                <EarningBreakdown earningBreakdown={getEarningBreakdown(selectedTransaction)} />

                                                            </div>

                                                        )}

                                                    </div>

                                                </div>

                                            </div>

                                        </div>

                                    )}

                                    {/* Pop up for uploading print data per print area for a selected order item - MVP
                                    {uploadPrintDataPopupOpen && (printDataPopUpDetails !== null) && (

                                        <UploadOrderItemPrintDataPopUp
                                            isOpen={uploadPrintDataPopupOpen}
                                            onClose={() => setUploadPrintDataPopupOpen(false)}
                                            orderItemID={printDataPopUpDetails.orderItemId}
                                            orderID={formValues.id}
                                            // Send the sku of the canonical product
                                            canonicalProductID={printDataPopUpDetails.canonicalProductSku}
                                            existingPrintData={printDataPopUpDetails.existingPrintAreasData}
                                            // Fetch order
                                            fetchOrders={fetchOrders}
                                            setUploadPrintDataPopupOpen={setUploadPrintDataPopupOpen}
                                        />

                                    )}
                                    */}
                                    {/* Pop up for selecting canonical POD Products to add as new order items 
                                    {canonicalProductsListPopUp && (

                                        <SelectCanonicalProductPopUp
                                            isOpen={canonicalProductsListPopUp}
                                            onClose={() => setCanonicalProductsListPopUp(false)}
                                            canonicalProducts={canonicalProducts || []}
                                            handleAddPODProductOrderItems={handleAddPODProductOrderItems}
                                        />

                                    )}
                                    */}

                                    {/* Pop up to show carousel of print area images */}
                                    {imagePreviewPopupOpen && (

                                        <ImageCarouselPopup
                                            images={orderItemImages}
                                            initialImage={selectedOrderItemImage}
                                            onClose={() => setImagePreviewPopupOpen(false)}
                                        />

                                    )}

                                </form>

                            </>

                        }

                    </div>

                    <div className="tabs-footer tabs-footer--no-background">

                        {/* Show the disabled delete button */}
                        {!addingNewOrder && deleteOrderAction &&

                            <CreateButton
                                title="Delete"
                                isDelete={true}
                                size="m"
                                type="button"
                                color="#d9534f"
                                disabled={true}
                                tooltip="Order deletion is currently disabled"
                            />

                        }

                        
                        {/* Show the cancel button */}
                        {!addingNewOrder &&

                            <CreateButton
                                title={cancellingOrder ? "Cancelling..." : "Cancel"}
                                size="m"
                                type="button"
                                color="#d9534f"
                                disabled={loading || cancellingOrder || (formValues?.cancelleddate && formValues?.cancelleddate !== "0001-01-01 00:00:00")}
                                tooltip={(formValues?.cancelleddate && formValues?.cancelleddate !== "0001-01-01 00:00:00") ? "Order has already been cancelled" : "Cancel and refund the order"}
                                onClick={() => handleCancelOrder(orderDetails.id)}
                            />

                        }

                        {/* Show the refund button for paid Stripe and PayPal orders */}
                        {!addingNewOrder && deleteOrderAction &&

                            <CreateButton
                                title={refundingOrder ? "Refunding..." : "Refund"}
                                size="m"
                                type="button"
                                //onClick={handleRefundOrder}
                                onClick={() => setRefundPopupOpen(true)}
                                disabled={
                                    loading 
                                    || refundingOrder 
                                    || isFormPartlyEdited()
                                    || formValues?.paiddate === "0001-01-01 00:00:00"
                                    || orderHasBeenFullyRefunded
                                    || (formValues?.paymentmethod !== "Stripe" && formValues?.paymentmethod !== "PayPal")
                                }
                                tooltip={isFormPartlyEdited() ? "Save or discard changes before refunding this order" : "Request refund"}
                            />

                        }

                        {/* Only show the save action when there is something to save. */}
                        {(addingNewOrder || isFormPartlyEdited() || loading) &&

                            <CreateButton
                                title={
                                    loading
                                        ? addingNewOrder ? "Creating..." : "Saving..."
                                        : addingNewOrder ? "Create Order" : "Save changes"
                                }
                                size="m"
                                type="button"
                                onClick={handleSubmit}
                                disabled={loading}
                            />

                        }

                        {/* Close the popup, warning first if there are unsaved changes. */}
                        <CreateButton 
                            title="Close"
                            size="m" 
                            invert 
                            type="button" 
                            onClick={() => {handleCloseButton("You have unsaved changes. Are you sure you want to close?")}} 
                            disabled={loading} 
                        />
                          
                    </div>

                </div>

            </div>


            <RequestFulfilmentPopUp
                isOpen={requestFulfilmentPopupOpen}
                onClose={() => setRequestFulfilmentPopupOpen(false)}
                orderDetails={orderDetails}

                serviceCodes={serviceCodes}
                setRequestFulfilmentPopupOpen={setRequestFulfilmentPopupOpen}
                fetchOrders={fetchOrders}
                fulfilmentRequestCosts={fulfilmentRequestCosts}
                setFulfilmentRequestCosts={setFulfilmentRequestCosts}
                FulfilmentCostBreakdownTable={FulfilmentCostBreakdownTable}
            />
                                      

            <FulfilmentDetailsPopUp
                isOpen={fulfilmentDetailsPopupOpen}
                onClose={() => {
                    // Close the pop up
                    setFulfilmentDetailsPopupOpen(false);
                    // Clear the current selected fulfilment details on pop up close
                    setFulfilmentDetails([]);
                }}
                fulfilmentDetails={fulfilmentDetails}
                orderDetails={orderDetails}
                FulfilmentCostBreakdownTable={FulfilmentCostBreakdownTable}
            />


            {refundPopupOpen && (

                <RefundOrderPopUp
                    orderDetails={formValues}
                    orderCurrency={orderCurrency}
                    refundingOrder={refundingOrder}
                    onClose={() => setRefundPopupOpen(false)}
                    onRefund={handleRefundOrder}
                    refundShipping={refundShipping}
                    setRefundShipping={setRefundShipping}
                    coreCurrencies={currencies}
                    currentRefundedAmount={totalRefundedBreakdown.inc_tax}
                />

            )}


        </div>

    );
};

export default OrderDetailsPopup;